import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { authService } from "@/services/api";
import { AuthShell, Field } from "./AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, CheckCircle2, ArrowLeft, LinkIcon } from "lucide-react";

const MIN_LENGTH = 8; // matches the backend's own check

export default function ResetPassword() {
  const navigate = useNavigate();
  // The token arrives as ?token=... on the link in the reset email. This page
  // previously ignored it entirely and posted only the new password, which is
  // why the flow could never have worked against a real backend.
  const [params] = useSearchParams();
  const token = params.get("token");

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // No token at all means the link was truncated by a mail client or typed by
  // hand. Say so here rather than letting them fill the form and fail on submit.
  if (!token) {
    return (
      <AuthShell testId="reset-page">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-amber-400/25 bg-amber-400/10 text-amber-400">
          <LinkIcon className="h-6 w-6" />
        </div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Link is incomplete</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          This reset link is missing its token — it may have been cut off when it was copied.
          Request a new one and open it directly from the email.
        </p>
        <Link to="/forgot-password">
          <Button className="mt-6" data-testid="reset-request-new">Request a new link</Button>
        </Link>
        <Link to="/login" className="mt-6 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
      </AuthShell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (pw.length < MIN_LENGTH) {
      setError(`Password must be at least ${MIN_LENGTH} characters`);
      return;
    }
    if (pw !== pw2) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword(token, pw);
      // Inline, not a toast: the toaster is mounted on the app shell, and this
      // navigates away immediately, so a toast here is a coin flip.
      navigate("/login?reset=1", { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || "Could not update your password. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // A used, expired or tampered token all come back with the same message from
  // the backend, on purpose — so the only useful thing to offer is a new link.
  const tokenRejected = error?.toLowerCase().includes("invalid or has expired");

  return (
    <AuthShell testId="reset-page">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-border/60 bg-secondary/40 text-primary">
        <CheckCircle2 className="h-6 w-6" />
      </div>
      <h1 className="font-display text-2xl font-semibold tracking-tight">Set a new password</h1>
      <p className="mt-1.5 text-sm text-muted-foreground">
        Choose a strong password for your ARC Beacon account. This link works once.
      </p>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <Field label="New password">
          <Input
            type="password" value={pw} onChange={(e) => { setPw(e.target.value); setError(null); }}
            placeholder="••••••••" required minLength={MIN_LENGTH} className="h-11"
            autoComplete="new-password" data-testid="reset-pw"
          />
        </Field>
        <Field label="Confirm password">
          <Input
            type="password" value={pw2} onChange={(e) => { setPw2(e.target.value); setError(null); }}
            placeholder="••••••••" required className="h-11"
            autoComplete="new-password" data-testid="reset-pw2"
          />
        </Field>

        {error && (
          <div className="rounded-xl border border-red-500/25 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="reset-error">
            {error}
            {tokenRejected && (
              <Link to="/forgot-password" className="ml-1 font-medium text-red-200 underline">
                Request a new link
              </Link>
            )}
          </div>
        )}

        <Button type="submit" className="h-11 w-full" disabled={loading} data-testid="reset-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
        </Button>
      </form>
      <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to sign in
      </Link>
    </AuthShell>
  );
}
